import React from 'react';
import PropTypes from 'prop-types';
import Box from 'common/src/components/Box';
import Text from 'common/src/components/Text';
import Heading from 'common/src/components/Heading';
import Container from 'common/src/components/UI/Container';
import { useTranslation } from 'react-i18next';

const ComparisonTable = ({
  sectionWrapper,
  titleStyle,
  descriptionStyle,
  tableWrapper,
  headerRow,
  row,
  featureCell,
  psonoCell,
  chromeCell,
  checkMark,
  crossMark,
  features,
  browserName,
  title,
}) => {
  const { t } = useTranslation();

  return (
    <Box {...sectionWrapper} as="section">
      <Container>
        <Heading
          {...titleStyle}
          content={
            title ||
            t(
              'Top reasons to choose Psono over Google Chrome native password manager'
            )
          }
        />

        <Box {...tableWrapper}>
          {/* Table Header */}
          <Box {...headerRow}>
            <Box {...featureCell} width={[1, 1 / 2]}>
              <Text
                {...descriptionStyle}
                content={t('Features')}
                fontWeight="700"
              />
            </Box>
            <Box {...psonoCell} width={[1 / 2, 1 / 4]}>
              <Text
                {...descriptionStyle}
                content="Psono"
                fontWeight="700"
                textAlign="center"
              />
            </Box>
            <Box {...chromeCell} width={[1 / 2, 1 / 4]}>
              <Text
                {...descriptionStyle}
                content={browserName || 'Chrome'}
                fontWeight="700"
                textAlign="center"
              />
            </Box>
          </Box>

          {/* Table Rows */}
          {features.map((feature, index) => (
            <Box {...row} key={`comparison-${index}`}>
              <Box {...featureCell} width={[1, 1 / 2]}>
                <Heading
                  as="h4"
                  content={t(feature.title)}
                  fontSize="18px"
                  fontWeight="600"
                  mb="10px"
                />
                <Text {...descriptionStyle} content={t(feature.description)} />
              </Box>
              <Box {...psonoCell} width={[1 / 2, 1 / 4]}>
                <Text
                  {...(feature.psonoValue === true ? checkMark : crossMark)}
                  content={
                    feature.psonoValue === true
                      ? '✓'
                      : feature.psonoValue || '-'
                  }
                  textAlign="center"
                />
              </Box>
              <Box {...chromeCell} width={[1 / 2, 1 / 4]}>
                <Text
                  {...(feature.chromeValue === true ? checkMark : crossMark)}
                  content={
                    feature.chromeValue === true
                      ? '✓'
                      : feature.chromeValue || '-'
                  }
                  textAlign="center"
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

ComparisonTable.propTypes = {
  sectionWrapper: PropTypes.object,
  titleStyle: PropTypes.object,
  descriptionStyle: PropTypes.object,
  tableWrapper: PropTypes.object,
  headerRow: PropTypes.object,
  row: PropTypes.object,
  featureCell: PropTypes.object,
  psonoCell: PropTypes.object,
  chromeCell: PropTypes.object,
  checkMark: PropTypes.object,
  crossMark: PropTypes.object,
  features: PropTypes.array,
  browserName: PropTypes.string,
  title: PropTypes.string,
};

ComparisonTable.defaultProps = {
  sectionWrapper: {
    pt: ['50px', '50px', '50px', '50px', '80px'],
    pb: ['50px', '50px', '60px', '70px', '80px'],
  },
  titleStyle: {
    fontSize: ['24px', '26px', '28px', '30px', '36px'],
    fontWeight: '600',
    color: '#0f2137',
    letterSpacing: '-0.025em',
    mb: '40px',
    textAlign: 'center',
    lineHeight: '1.3',
  },
  descriptionStyle: {
    fontSize: '15px',
    fontWeight: '400',
    color: '#5d646d',
    lineHeight: '1.75',
    mb: '0',
  },
  tableWrapper: {
    backgroundColor: '#fff',
    borderRadius: '10px',
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
  },
  headerRow: {
    flexBox: true,
    flexWrap: 'wrap',
    backgroundColor: '#f7f8fb',
    p: ['15px', '20px', '25px'],
    borderBottom: '2px solid #e9ecf2',
  },
  row: {
    flexBox: true,
    flexWrap: 'wrap',
    p: ['15px', '20px', '25px'],
    borderBottom: '1px solid #e9ecf2',
    alignItems: 'center',
  },
  featureCell: {
    pr: ['10px', '20px'],
  },
  psonoCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chromeCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkMark: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2aa275',
  },
  crossMark: {
    fontSize: '20px',
    fontWeight: '400',
    color: '#d0d0d0',
  },
  features: [],
};

export default ComparisonTable;
